import { useMemo, useState, useEffect } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import InstantMeetingDialog from "./components/meetingDashboard/instant-meeting-dialog"
import JoinMeetingDialog from "./components/meetingDashboard/JoinMeetingDialog"
import MeetingDetails from "./components/meetingDashboard/MeetingDetails"
import AdvancedCalendar from "./components/meetingDashboard/advanced-calendar"
import ModernActionButtons from "./components/meetingDashboard/modern-action-buttons"
import MeetingList from "./components/meetingDashboard/MeetingList"
import TotalMeetingsCard from "./components/meetingDashboard/TotalMeetingsCard"
import MeetingsDurationCard from "./components/meetingDashboard/MeetingsDurationCard"
import SubHeader from "@/clientWeb/components/layout/SubHeader"
import { useDeviceDetection } from "@/clientVc/viewmodels/hooks/useDeviceDetection"
import { useMeetingsStore } from "./store/meetings.store"

/* ─── useNow Hook ───────────────────────────────────────────────── */
function useNow() {
  const [now, setNow] = useState(new Date())
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])
  return now
}

export { useNow }

/* ─── Main Dashboard Content ───────────────────────────────────── */
export default function MeetingsPage() {
  const { isTablet } = useDeviceDetection()
  const t = useTranslation()
  const meetings = useMeetingsStore((s) => s.meetings)
  const fetchFromApi = useMeetingsStore((s) => s.fetchFromApi)
  const [instantDialogOpen, setInstantDialogOpen] = useState(false)

  // Loaded on open, then kept current: a list fetched once went on offering
  // Join on a meeting the host had since marked as past, until a reload. It
  // refreshes when the tab comes back into view and once a minute while shown.
  useEffect(() => {
    void fetchFromApi()
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") void fetchFromApi()
    }
    const timer = setInterval(refreshIfVisible, 60_000)
    document.addEventListener("visibilitychange", refreshIfVisible)
    window.addEventListener("focus", refreshIfVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener("visibilitychange", refreshIfVisible)
      window.removeEventListener("focus", refreshIfVisible)
    }
  }, [])
  const [joinDialogOpen, setJoinDialogOpen] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState<any>(null)
  const [showDetails, setShowDetails] = useState(false)

  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const normalizedSelectedDate = useMemo(() => {
    const d = new Date(selectedDate)
    d.setHours(0, 0, 0, 0)
    return d
  }, [selectedDate])
  return (
    <>
    
      {/* ── Body: responsive layout ──────────────── */}
      <div className="flex flex-col flex-1 gap-3 sm:gap-4 px-1 pb-0 md:px-4 pt-2 sm:pt-2 sm:pb-4 min-h-0 overflow-hidden">
  <SubHeader 
          moduleName={t('meetings.title')}
          description={''}
          OptionalAction={true}
        />
        {/* ── MOBILE & TABLET PORTRAIT: Stack vertically ───────────────────────────────────── */}
        <div className="flex flex-col h-full md:landscape:hidden lg:hidden w-full gap-3 min-h-0 overflow-hidden">
          {/* Quick Actions */}
          <ModernActionButtons
            onInstantClick={() => setInstantDialogOpen(true)}
            onJoinClick={() => setJoinDialogOpen(true)}
          />
          
          {!showDetails ? (
            <>
              {/* Analytics Button - mobile (removed duplicate) */}
              {/* Meeting List */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <MeetingList onMeetingClick={(meeting) => {
                  setSelectedMeeting(meeting)
                  setShowDetails(true)
                }} selectedDate={normalizedSelectedDate} />
              </div>
              
              {/* Stats Cards - hidden on mobile */}
              <div className="hidden lg:flex gap-3 min-h-0">
                <TotalMeetingsCard />
                <MeetingsDurationCard />
              </div>
              {/* Stat cards row */}
            </>
          ) : (
            <div className="flex-1 min-h-0 overflow-hidden">
              <MeetingDetails
                meeting={selectedMeeting}
                onBack={() => setShowDetails(false)}
              />
            </div>
          )}
        </div>

        {/* ── TABLET LANDSCAPE & DESKTOP: Side-by-side layout ───────────────────────────────── */}
        <div className="hidden h-full md:landscape:flex lg:flex w-full gap-3 xl:gap-4 min-h-0 overflow-hidden">
          {/* LEFT PANEL */}
          <div className="flex flex-col gap-3 xl:gap-3.5 min-w-0 overflow-hidden w-full lg:max-w-[54%] xl:max-w-[620px] xl:w-[620px] lg:flex-shrink-0">
            {/* Modern Quick Actions */}
            <ModernActionButtons
              onInstantClick={() => setInstantDialogOpen(true)}
              onJoinClick={() => setJoinDialogOpen(true)}
            />

            {/* Meeting rows */}
            <div className="flex-1 min-h-0 overflow-hidden">
              <MeetingList onMeetingClick={(meeting) => {
                setSelectedMeeting(meeting)
                setShowDetails(true)
              }} selectedDate={normalizedSelectedDate} />
            </div>
          </div>

          {/* RIGHT PANEL — Calendar or Details */}
          <div className="flex-1 flex flex-col gap-3 xl:gap-3.5 min-w-0 overflow-hidden">
            {!showDetails ? (
              <div className="flex flex-col gap-3 xl:gap-3.5 h-full min-h-0 overflow-y-auto overflow-x-hidden pr-1">
                {/* Analytics Quick Link */}
                {/* <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline" size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => navigate('/analytics')}
                  >
                    <BarChart2 className="w-3.5 h-3.5" /> Analytics
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => navigate('/analytics/recordings')}
                  >
                    <BarChart2 className="w-3.5 h-3.5" /> Recordings
                  </Button>
                </div> */}
                {/* Calendar */}
                <div className={`min-h-[320px] lg:min-h-[360px] ${isTablet ? '' : 'min-h-0'} xl:flex-[7] overflow-hidden`}>
                  <AdvancedCalendar
                    meetings={meetings}
                    selectedDate={normalizedSelectedDate}
                    onSelectDate={setSelectedDate}
                  />
                </div>
                {/* Stats row */}
                <div
                  className="flex flex-col gap-2 xl:gap-3 animate-fade-slide-up shrink-0"
                  style={{  animation: "fadeSlideUp 0.4s ease 0.45s both" }}
                >
                  {/* <MeetingStatsRow /> */}
                  <div className={`grid grid-cols-1 gap-2 xl:gap-3 ${isTablet ? 'grid-cols-1' : 'grid-cols-2'}`}>
                    <TotalMeetingsCard />
                    <MeetingsDurationCard />
                  </div>
                </div>
              </div>
            ) : (
              <MeetingDetails
                meeting={selectedMeeting}
                onBack={() => setShowDetails(false)}
              />
            )}
          </div>
        </div>
      </div>
      
     <InstantMeetingDialog 
        open={instantDialogOpen} 
        onOpenChange={setInstantDialogOpen} 
      />
      <JoinMeetingDialog 
        open={joinDialogOpen} 
        onOpenChange={setJoinDialogOpen} 
      />
    </>
  )
}